import { subDays } from 'date-fns';
import { cloneDeep } from 'lodash';
// Types.
import { Contract, Delivery } from '../types/contract';
import { SelectedMeteringPoint } from '../types/consumptionRates';
import { AvailableUsers } from '../types/customer';
// Utils.
import { deliveryIsEnergyManagement, deliveryIsPriceFix, filterDelivery } from '../components/Contracts/ContractDetails';

export const filterOutUndefinedContracts = (value: Contract | undefined): value is Contract => {
  return value !== undefined;
};

export const filterOutContractsWithSameStartAndEndAndStateS = (contract: Contract): contract is Contract => {
  return (
    (contract.contractStatus.contractStatusNo.toLowerCase() !== 's' && contract.contractStatus.contractStatusNo.toLowerCase() !== 'o') ||
    contract.startDate !== contract.endDate
  );
};

// These are the contract groups for "other" contract types. Sadly there is no logic here this is just what they are.
const otherContractGroupsRet = [10, 22, 23, 24, 25, 31];
const otherContractGroupsDso = [10, 22, 33, 31];

const otherContractGroupsEV = [26, 36];

export const contractIsOtherType = (contract: Contract): boolean => {
  const contractIsOtherType =
    (contract.endpoint?.toLowerCase() === 'ret'
      ? otherContractGroupsRet.includes(contract.contractType.contractTypeNo)
      : otherContractGroupsDso.includes(contract.contractType.contractTypeNo)) ||
    otherContractGroupsEV.includes(contract.contractType.contractTypeNo);
  if (contractIsOtherType) {
    return true;
  }
  return false;
};

export const getSelectedMeteringPoint = (suppliedContracts: Contract[]): SelectedMeteringPoint | undefined => {
  let selectedMeteringPoint: SelectedMeteringPoint | undefined = undefined;

  for (const contract of suppliedContracts) {
    const status = contract.contractStatus.contractStatusNo.toLowerCase();
    if (contract.meteringPointId && status !== 's' && status !== 'o') {
      // merge contracts if same metering point id
      const contractsWithThisMeteringPoint = suppliedContracts
        .filter((c: Contract) => c.meteringPointId === contract.meteringPointId)
        .sort((a, b) => (a.startDate > b.startDate ? 1 : -1));

      const cm = contractsWithThisMeteringPoint;

      const mergedContract = cm.length > 1 ? { ...cm[0], contractNo: cm[cm.length - 1].contractNo, endDate: cm[cm.length - 1].endDate } : cm[0];

      selectedMeteringPoint = {
        customerId: mergedContract.customerId,
        contractCode: mergedContract.contractCode,
        contractNo: mergedContract.contractNo,
        meteringPointId: mergedContract.meteringPointId ?? '',
      };
      break;
    }
    // show consumption for users that only have contracts that have ended
    else if (
      contract.meteringPointId &&
      contract.startDate &&
      contract.endDate &&
      suppliedContracts.filter(
        (c) => c.contractStatus.contractStatusNo.toLowerCase() !== 's' && c.contractStatus.contractStatusNo.toLowerCase() !== 'o',
      ).length === 0
    ) {
      selectedMeteringPoint = {
        customerId: contract.customerId,
        contractCode: contract.contractCode,
        contractNo: contract.contractNo,
        meteringPointId: contract.meteringPointId,
      };
    }
  }
  return selectedMeteringPoint;
};

export const getContractDateTo = (contract: Contract): { dateTo: Date | undefined; dateIsLastFix: boolean } => {
  let dateIsLastFix = false;
  // If the supplied contract has an end date, it has ended so we return that date.
  if (contract.endDate) {
    return { dateTo: new Date(contract.endDate), dateIsLastFix };
  }
  // Check if we have a fixed price element.
  let dateTo: Date | undefined = undefined;
  let dateToLocked = false;
  contract.activeDeliveries?.map((delivery): undefined => {
    let fixedPricedelivery = false;

    if (delivery.elementNo === 10) {
      fixedPricedelivery = true;
    }

    if (deliveryIsEnergyManagement(delivery)) {
      dateTo = subDays(new Date(delivery.tariffToDate), 1);
      dateToLocked = true;
    } else if (fixedPricedelivery && deliveryIsPriceFix(delivery)) {
      dateTo = subDays(new Date(delivery.specialToDate || new Date()), 1);
      dateToLocked = true;
      dateIsLastFix = true;
    } else if (!filterDelivery(delivery) && !dateToLocked) {
      dateTo = subDays(new Date(delivery.tariffToDate), 1);
    }

    return undefined;
  });

  return { dateTo, dateIsLastFix };
};

/**
 * Determines based off of the many rules if a specific delivery should be displayed or not.
 *
 * @param delivery an individual delivery that we need to determine if we should show or not.
 */
export const shouldDisplayDelivery = (delivery: Delivery): boolean => {
  let shouldBeDisplayed = false;

  if (delivery.toDate && new Date(delivery.toDate) > new Date()) {
    shouldBeDisplayed = true;
  }

  return shouldBeDisplayed;
};

/**
 * Determines based off of available users if there is an issue with the account and thus which message we should show the user.
 *
 * @param users
 */
export const accountError = (users: AvailableUsers): boolean => {
  let isError = false;
  if (users.parentUser.userId === 0 && (!users.childUsers || users.childUsers.length === 0)) {
    isError = true;
  }

  return isError;
};

export const contractRename = (suppliedGroupName: string): string => {
  const replaceContractNames: any = {
    'loiste sähkösopimus': 'Sähkösopimus',
    'loiste vakuudet': 'Vakuus',
    'eko sähkösopimus': 'Sähkösopimus',
    'lsv siirtosopimus': 'Verkkopalvelusopimus',
    'lsv liittymissopimus': 'Liittymissopimus',
    'lsv vakuudet': 'Vakuus',
    'lol liittymäsopimus,lämpö': 'Kaukolämmön liittymissopimus',
    'lol kaukolämpösopimus': 'Kaukolämmön toimitussopimus',
    'eko vakuudet': 'Vakuus',
    aurinkopaneeli: 'Aurinkopaneeli',
  };
  const regex = new RegExp(Object.keys(replaceContractNames).join('|'), 'gi');
  const group = cloneDeep(suppliedGroupName).toLowerCase();

  let groupName = group.replace(regex, function (match): any {
    return replaceContractNames[match];
  });
  if (suppliedGroupName.toLocaleLowerCase() === groupName) {
    groupName = suppliedGroupName;
  }

  return groupName;
};

export const alphabeticallySortContracts = (a: Contract, b: Contract): number => {
  const nameA = a.meteringPoint?.streetName?.toLowerCase();
  const nameB = b.meteringPoint?.streetName?.toLowerCase();

  if (nameA && nameB) {
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  } else {
    return 0;
  }
};

export const PriceFixCampaignContractTemplateNo = import.meta.env.VITE_ENV === 'dev' ? 269 : 235;
export const PriceFixTariffNo = import.meta.env.VITE_ENV === 'dev' ? 172 : 162;
